import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@services/api/client";
import "@styles/FoodCourtApprovalNew.css";

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

interface PendingFC {
  foodCourtId: string;
  name: string;
  email: string;
  contactNumber: string;
  address: string;
  city: string;
  status: string;
  fssaiNumber: string;
  panNumber: string;
  gstin: string;
  typeOfFood: string;
}

const FoodCourtApproval: React.FC = () => {
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<PendingFC[]>({
    queryKey: ["pendingFCs"],
    queryFn: async () => {
      const res = await apiClient.get("/foodcourt/admin/foodcourts/pending", authHeaders());
      return Array.isArray(res.data) ? res.data : [];
    },
    refetchOnWindowFocus: false,
  });

  const approve = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.put(`/foodcourt/admin/foodcourts/${id}/approve`, null, authHeaders());
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pendingFCs"] }),
  });

  const reject = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await apiClient.put(`/foodcourt/admin/foodcourts/${id}/reject?rejectionReason=${encodeURIComponent(reason)}`, null, authHeaders());
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pendingFCs"] }); setRejectId(null); setRejectReason(""); },
  });

  const pending = Array.isArray(data) ? data : [];
  const filtered = pending.filter(fc => !search || fc.name?.toLowerCase().includes(search.toLowerCase()) || fc.email?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fca">
      <div className="fca-header">
        <div>
          <h1 className="fca-title">Pending Approvals</h1>
          <p className="fca-subtitle">Review and approve new food court registrations</p>
        </div>
        <div className="fca-count-badge">{pending.length} pending</div>
      </div>

      {pending.length > 0 && (
        <div className="fca-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input type="text" placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      )}

      {isLoading ? (
        <div className="fca-loading">Loading pending food courts...</div>
      ) : filtered.length === 0 ? (
        <div className="fca-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
          <p>{pending.length === 0 ? "🎉 No pending food courts — all caught up!" : `No results for "${search}"`}</p>
        </div>
      ) : (
        <div className="fca-list">
          {filtered.map(fc => (
            <div key={fc.foodCourtId} className={`fca-card ${expandedId === fc.foodCourtId ? "expanded" : ""}`}>
              <div className="fca-card-main" onClick={() => setExpandedId(expandedId === fc.foodCourtId ? null : fc.foodCourtId)}>
                <div className="fca-card-left">
                  <h3 className="fca-card-name">{fc.name}</h3>
                  <div className="fca-card-meta">
                    {fc.city && <span>📍 {fc.city}</span>}
                    {fc.typeOfFood && <span>🍽️ {fc.typeOfFood}</span>}
                  </div>
                </div>
                <div className="fca-card-right">
                  <span className="fca-pending-badge">PENDING</span>
                  <svg className={`fca-chevron ${expandedId === fc.foodCourtId ? "open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>

              {expandedId === fc.foodCourtId && (
                <div className="fca-card-details">
                  <div className="fca-detail-grid">
                    <div className="fca-detail-item"><span className="fca-detail-label">Email</span><span>{fc.email || "—"}</span></div>
                    <div className="fca-detail-item"><span className="fca-detail-label">Phone</span><span>{fc.contactNumber || "—"}</span></div>
                    <div className="fca-detail-item"><span className="fca-detail-label">Address</span><span>{fc.address || "—"}</span></div>
                    <div className="fca-detail-item"><span className="fca-detail-label">FSSAI</span><span>{fc.fssaiNumber || <span className="fca-missing">Not provided</span>}</span></div>
                    <div className="fca-detail-item"><span className="fca-detail-label">PAN</span><span>{fc.panNumber || <span className="fca-missing">Not provided</span>}</span></div>
                    <div className="fca-detail-item"><span className="fca-detail-label">GSTIN</span><span>{fc.gstin || <span className="fca-missing">Not provided</span>}</span></div>
                  </div>

                  <div className="fca-card-actions">
                    <button className="fca-btn approve" onClick={() => approve.mutate(fc.foodCourtId)} disabled={approve.isPending}>
                      {approve.isPending ? "Approving..." : "✓ Approve"}
                    </button>
                    <button className="fca-btn reject" onClick={() => { setRejectId(fc.foodCourtId); setRejectReason(""); }}>
                      ✗ Reject
                    </button>
                  </div>
                </div>
              )}

              {rejectId === fc.foodCourtId && (
                <div className="fca-reject-form">
                  <textarea placeholder="Reason for rejection..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={2} />
                  <div className="fca-reject-actions">
                    <button className="fca-btn cancel" onClick={() => setRejectId(null)}>Cancel</button>
                    <button className="fca-btn reject-confirm" disabled={!rejectReason.trim() || reject.isPending} onClick={() => reject.mutate({ id: fc.foodCourtId, reason: rejectReason })}>
                      {reject.isPending ? "Rejecting..." : "Confirm Reject"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FoodCourtApproval;
